import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { ShieldCheck, Settings, Check, Languages } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useProjectName } from './ProjectNameContext';
import { useDataContext } from '@/lib/DataContext';
import { useThemeMode } from '@/lib/ThemeContext';
import { ROLE } from '@/lib/constants';

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { projectName, appId, clearProject } = useProjectName();
  const { data } = useDataContext();
  const { themeMode, setThemeMode } = useThemeMode();
  const { t, i18n } = useTranslation();
  const isDark = themeMode === 'dark';
  const isLight = themeMode === 'light';
  const headerDark = themeMode !== 'light';

  const userEmail = data?.user_email || '';
  const userName = data?.name || data?.userDetails?.name || userEmail;
  const userRole = data?.roles?.length ? data.roles[0].replace(/_/g, ' ') : '';
  const isAdmin = data?.roles?.includes(ROLE.SENTINEL_POWER_USER);
  const isAdminPage = location.pathname.startsWith('/admin');

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClick = (_event: Event | React.SyntheticEvent, action: string) => {
    switch (action) {
      case 'ADMIN':
        navigate('/admin');
        break;
      case 'PROJECT_SETTINGS':
        navigate(`/project/${appId}/settings`);
        break;
      case 'EXIT_APP':
        window.location.href = '/';
        break;
      case 'LOGOUT':
        auth.signoutRedirect();
        break;
      default:
        break;
    }
    setOpen(false);
  };

  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: isLight ? '#fff' : isDark ? '#0A0A0F' : '#000',
        borderRadius: 1.5,
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : isLight ? '1px solid #e0e0e0' : 'none',
      }}
      elevation={0}
    >
      <Toolbar sx={{ px: '15px !important', position: 'relative' }}>
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="header-icon-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#46adc8" />
              <stop offset="100%" stopColor="#a4e0aa" />
            </linearGradient>
          </defs>
        </svg>
        {/* Left side - App name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Tooltip title={t('nav.goToHome')}>
            <Typography
              variant="h5"
              noWrap
              onClick={() => { clearProject(); navigate('/'); }}
              sx={{ fontSize: 20, fontWeight: 700, color: headerDark ? '#fff' : '#111', cursor: 'pointer' }}
            >
              <Stack direction="row" sx={{ fontSize: 22 }}>
                <span style={{ fontWeight: 200 }}>Sent</span>
                <span>inel</span>
              </Stack>
            </Typography>
          </Tooltip>
          {(isAdminPage || projectName) && (
            <>
              <Divider orientation="vertical" sx={{ height: 20, borderColor: headerDark ? '#bbb8b8' : '#ccc' }} />
              <Typography
                noWrap
                sx={{
                  fontSize: 18,
                  fontWeight: 200,
                  color: headerDark ? '#fff' : '#333',
                  maxWidth: { xs: 90, sm: 200 },
                }}
              >
                {isAdminPage ? 'Admin Panel' : projectName}
              </Typography>
            </>
          )}
        </Box>

        {/* Center - Logo (absolutely positioned, hidden on narrow phones to avoid overlap) */}
        <Tooltip title={t('nav.goToHome')}>
          <Box
            onClick={() => { clearProject(); navigate('/'); }}
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              cursor: 'pointer',
            }}
          >
            <img
              src="/logowhite.svg"
              alt="Sentinel Logo"
              style={{ height: '56px', width: 'auto', filter: headerDark ? 'none' : 'brightness(0)' }}
            />
          </Box>
        </Tooltip>

        {/* Right side - Admin icon + Profile */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, justifyContent: 'flex-end' }}>
          {projectName && appId && (
            <Tooltip title="Project Settings">
              <IconButton
                onClick={(e) => handleClick(e, 'PROJECT_SETTINGS')}
                disableTouchRipple
                disableFocusRipple
                disableRipple
                sx={{
                  background: headerDark ? '#ffffff2e' : 'rgba(0,0,0,0.06)',
                  color: headerDark ? '#fff' : '#333',
                  padding: '5px',
                  '&:hover': { background: headerDark ? '#ffffff2e' : 'rgba(0,0,0,0.1)', '& svg': { stroke: 'url(#header-icon-gradient)' } },
                }}
              >
                <Settings size={20} />
              </IconButton>
            </Tooltip>
          )}
          <div>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              ref={anchorRef}
              onClick={handleToggle}
              sx={{ cursor: 'pointer', borderRadius: '20px', px: 1.25, py: 0.4, '&:hover': { background: headerDark ? '#ffffff2e' : 'rgba(0,0,0,0.06)' } }}
            >
              <Stack direction="column" alignItems="flex-end" spacing={0} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <Typography sx={{ fontSize: '0.8rem', color: headerDark ? '#fff' : '#111', fontWeight: 500, lineHeight: 1.2 }}>
                  {userName}
                </Typography>
                {userRole && (
                  <Typography sx={{ fontSize: '0.6rem', color: headerDark ? '#ffffff99' : '#666', fontWeight: 400, lineHeight: 1.2, textTransform: 'capitalize' }}>
                    {userRole.toLowerCase()}
                  </Typography>
                )}
              </Stack>
              <Avatar sx={{ width: 26, height: 26, fontSize: '0.75rem', fontWeight: 600, bgcolor: headerDark ? '#ffffff3e' : 'rgba(0,0,0,0.08)', color: headerDark ? '#fff' : '#333', overflow: 'visible', position: 'relative', '&::before': { content: '""', position: 'absolute', inset: -2, borderRadius: '50%', padding: '1.5px', background: 'linear-gradient(90deg, #46adc8 0%, #a4e0aaf5 100%)', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude', pointerEvents: 'none' } }}>
                {userName ? userName.charAt(0).toUpperCase() : ''}
              </Avatar>
            </Stack>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="bottom-start"
              transition
              sx={{ zIndex: 1400 }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom-start' ? 'left top' : 'left bottom',
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                      <MenuList
                        autoFocusItem={open}
                        id="composition-menu"
                        aria-labelledby="composition-button"
                      >
                        {isAdminPage ? (
                          <MenuItem onClick={() => { setOpen(false); clearProject(); navigate('/'); }}>
                            Exit Admin Panel
                          </MenuItem>
                        ) : (
                          isAdmin && (
                            <>
                              <MenuItem onClick={() => { setOpen(false); navigate('/admin/projects'); }}>
                                <ListItemIcon sx={{ minWidth: '30px !important' }}>
                                  <ShieldCheck size={18} />
                                </ListItemIcon>
                                <ListItemText>Admin</ListItemText>
                              </MenuItem>
                              <Divider />
                            </>
                          )
                        )}
                        {appId && (
                          <>
                            <MenuItem onClick={(e) => handleClick(e, 'EXIT_APP')}>
                              Exit App
                            </MenuItem>
                            <Divider />
                          </>
                        )}
                        <MenuItem onClick={(e) => handleClick(e, 'LOGOUT')}>
                          Logout
                        </MenuItem>
                        <Divider />
                        <Box sx={{ px: 2, py: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>{t('common.language')}</Typography>
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {[
                              { code: 'en', label: 'EN' },
                              { code: 'de', label: 'DE' },
                            ].map(({ code, label }) => (
                              <Box
                                key={code}
                                onClick={() => i18n.changeLanguage(code)}
                                sx={{
                                  px: 1.5, py: 0.4, borderRadius: '6px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                  bgcolor: i18n.language === code ? 'primary.main' : 'action.selected',
                                  color: i18n.language === code ? 'primary.contrastText' : 'text.secondary',
                                  display: 'flex', alignItems: 'center', gap: 0.5,
                                }}
                              >
                                <Languages size={12} />
                                {label}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                        <Divider />
                        <Box sx={{ px: 2, py: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Theme</Typography>
                          <Stack direction="row" spacing={1.5} justifyContent="center">
                            {[
                              { mode: 'dark' as const, label: 'Dark', bg: '#0A0A0F', border: '#F5A623' },
                              { mode: 'bw' as const, label: 'B&W', bg: 'linear-gradient(135deg, #000 50%, #fff 50%)', border: '#888' },
                              { mode: 'light' as const, label: 'Light', bg: '#fff', border: '#ccc' },
                            ].map(({ mode, label, bg, border }) => (
                              <Stack key={mode} alignItems="center" spacing={0.5} onClick={() => { setThemeMode(mode); setOpen(false); }} sx={{ cursor: 'pointer' }}>
                                <Box sx={{
                                  width: 32, height: 32, borderRadius: '50%',
                                  background: bg,
                                  border: themeMode === mode ? `2.5px solid ${border}` : '2px solid transparent',
                                  boxShadow: themeMode === mode ? `0 0 0 2px ${border}40` : 'inset 0 0 0 1px rgba(128,128,128,0.3)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  transition: 'all 0.15s',
                                }}>
                                  {themeMode === mode && <Check size={14} style={{ color: mode === 'light' ? '#333' : '#fff' }} />}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: themeMode === mode ? 600 : 400 }}>{label}</Typography>
                              </Stack>
                            ))}
                          </Stack>
                        </Box>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </div>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
